import { useState } from "react";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg } from "../ui";
import { fmtTime } from "./meta";
import { StaxLiveTest } from "./StaxLiveTest";

function DailyNote({ on }) {
  return (
    <div className={`mt-1 text-[11px] ${on ? "text-emerald-700" : "text-slate-500"}`} data-testid="uc-daily-writes-state">
      Daily 6am run: {on ? "writes to Stax Bill" : "stays a dry run"} — change it in Settings.
    </div>
  );
}

/** The switch that lets usage runs write the amounts to Stax Bill (off = dry run). Super admins only.
 *  Switching on needs a successful one-domain live test first. */
export function StaxLiveControl({ ov, reload }) {
  const live = ov.config.stax_live;
  const tested = ov.config.live_test?.status === "updated";
  const [ask, setAsk] = useState(false);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (enabled) => {
    setBusy(true);
    try {
      await api.put("/usage-charges/stax-live", { enabled, confirm: ok });
      toast.success(enabled ? "Stax Bill updates are ON" : "Stax Bill updates are off — runs are dry runs again");
      setAsk(false); setOk(false); reload();
    } catch (e) { toast.error(errMsg(e, "Could not change it")); } finally { setBusy(false); }
  };
  const ch = ov.config.stax_live_changed;
  return (
    <div className="px-5 py-4" data-testid="uc-stax-live">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Stax Bill updates</div>
      <div className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${live ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="uc-stax-live-state">
        {live ? <ShieldAlert className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}{live ? "ON — runs write the amounts" : "Dry run — nothing is written"}
      </div>
      {ch && <div className="mt-1 text-[11px] text-slate-500">Switched {ch.enabled ? "on" : "off"} by {ch.by} · {fmtTime(ch.at)}</div>}
      {live && <DailyNote on={ov.config.daily_writes} />}
      {!ov.is_super && <p className="mt-2 text-[11px] text-slate-500">Only super admins can change this.</p>}
      {ov.is_super && !live && <StaxLiveTest ov={ov} reload={reload} />}
      {ov.is_super && (
        <Button size="sm" variant={live ? "outline" : "default"} className="mt-2" onClick={() => (live ? save(false) : setAsk(true))}
          disabled={busy || (!live && !tested)} title={!live && !tested ? "Run a successful live test on one domain first" : undefined} data-testid="uc-stax-live-toggle">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{live ? "Switch back to dry run" : "Step 2 · Switch Stax Bill updates on…"}</Button>
      )}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="uc-stax-live-dialog">
          <AlertDialogHeader><AlertDialogTitle>Write usage amounts to Stax Bill?</AlertDialogTitle>
            <AlertDialogDescription>From now on every usage run you start by hand sets the Long Distance and Toll Free Usage product of each domain's subscription to its cycle-to-date amount, included, quantity 1 — the amounts you see as “Would change” in the dry runs. The daily 6am run {ov.config.daily_writes ? "writes too" : "stays a dry run until you switch on “Daily run writes to Stax Bill” in Settings"}. Special billing customers are never written.</AlertDialogDescription></AlertDialogHeader>
          <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="uc-stax-live-confirm" />
            I checked the live test in Stax Bill and the dry-run amounts, and want live subscriptions changed.</label>
          <AlertDialogFooter><AlertDialogCancel data-testid="uc-stax-live-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); save(true); }} disabled={!ok || busy} data-testid="uc-stax-live-apply">Switch on</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
